import { useFlag } from '@automata/utils/hooks';
import startCase from 'lodash/startCase';
import { NodeProps } from 'reactflow';

import { getIndicators } from './utils';
import {
  selectDataHandleIsConnectedByHandleID,
  selectHandlePairIdOrderByNode,
} from '../../../../features/graph/selectors';
import {
  selectLabwareDisplayDataByTaskId,
  selectTaskDependenciesErrors,
} from '../../../../features/selectors';
import {
  selectDataExportOutputConnectorsById,
  selectInstrumentDataById,
  selectTaskPropertiesStrict,
} from '../../../../features/workflow/selectors';
import type { WorkflowSerializationError } from '../../../../features/workflowJsonSelectors';
import { selectWorkflowLevelErrorsForTaskId } from '../../../../features/workflowJsonSelectors/workflowLevelErrors';
import { useAppSelector } from '../../../../hooks';
import { Task, useAllTasks } from '../../../../hooks/tasks';
import {
  AllTaskProperties,
  CodeTaskProperties,
  ConditionalTaskProperties,
  ContinuousLoadingProperties,
  DataExportTaskProperties,
  InstrumentForLocation,
  PropertiesType,
} from '../../../../types';
import { DataNode } from '../../DataNode';
import NodeIndicators from '../../NodeIndicators';
import { ConditionNode } from '../ConditionNode';
import { ContinuousLoadingNode } from '../ContinuousLoadingNode';

import { TaskNode } from './index';

/** Returns true if a workflow-level error should be shown in a TaskNode for an action task. For filtering. */
export const isActionTaskError = (err: WorkflowSerializationError): boolean => {
  switch (err.message) {
    // Blocked
    case 'MISSING_TIME_ESTIMATE': {
      return false;
    }
    default: {
      return true;
    }
  }
};

export const ConnectedDataNode = ({
  selected,
  properties,
  task,
}: {
  selected: boolean;
  properties: CodeTaskProperties | DataExportTaskProperties;
  task: Task;
}) => {
  const workflowLevelErrorsVisible = useFlag(
    'fe.canvas-builder-workflow-errors-visible'
  );
  const workflowLevelErrors = useAppSelector((state) =>
    selectWorkflowLevelErrorsForTaskId(state, task.id)
  );
  const connectors = useAppSelector(selectDataExportOutputConnectorsById);
  const selectLabel =
    properties.propertiesType === PropertiesType.Code
      ? startCase(properties.scriptName)
      : connectors.find(
          (connector) => connector.id === properties.connectorType
        )?.name ?? 'No Connector Available';

  const taskDependencyErrors = useAppSelector(selectTaskDependenciesErrors);

  const indicators = (
    <NodeIndicators
      indicators={getIndicators({
        task,
        taskDependencyErrors,
        workflowLevelErrors: workflowLevelErrorsVisible
          ? workflowLevelErrors
          : [],
      })}
      rightAlign
    />
  );

  return (
    <DataNode
      properties={properties}
      selected={selected}
      indicators={indicators}
      selectLabel={selectLabel}
    />
  );
};

const PlaceholderTaskNode = ({
  selected,
  taskProperties,
}: {
  selected: boolean;
  taskProperties: AllTaskProperties;
}): JSX.Element | null => {
  if (taskProperties.propertiesType !== PropertiesType.Action) {
    return null;
  }

  const placeholderInstrument = {
    id: '__placeholder__',
    name: 'Select Instrument',
    driverName: 'placeholder',
    bench: 'placeholder',
    capabilities: [],
    capacity: 0,
    image: '',
    manufacturer: '',
    driverVersion: '',
    config: {},
  } as InstrumentForLocation;

  return (
    <TaskNode
      indicators={null}
      instrumentData={placeholderInstrument}
      labwareDataByHandle={{}}
      orderedHandlePairIds={[]}
      selected={selected}
      taskProperties={taskProperties}
    />
  );
};

export const ConnectedInstrumentTaskNode = ({
  id: _id,
  taskProperties,
  task,
  selected,
}: {
  id: string;
  taskProperties: AllTaskProperties;
  task: Task;
  selected: boolean;
}) => {
  const workflowLevelErrorsVisible = useFlag(
    'fe.canvas-builder-workflow-errors-visible'
  );
  const instrumentDataById = useAppSelector(selectInstrumentDataById);
  const labwareDisplayDataByTaskId = useAppSelector(
    selectLabwareDisplayDataByTaskId
  );
  const orderedHandlePairIdsByTask = useAppSelector(
    selectHandlePairIdOrderByNode
  );
  const taskDependencyErrors = useAppSelector(selectTaskDependenciesErrors);
  const workflowLevelErrors = useAppSelector((state) =>
    selectWorkflowLevelErrorsForTaskId(state, taskProperties.id)
  ).filter((err) => workflowLevelErrorsVisible && isActionTaskError(err.error));

  if (taskProperties.propertiesType !== PropertiesType.Action) {
    return null;
  }

  const { instrumentId } = taskProperties;
  const instrumentData =
    instrumentId === null ? null : instrumentDataById[instrumentId] ?? null;

  if (instrumentData === null) {
    return (
      <PlaceholderTaskNode
        selected={selected}
        taskProperties={taskProperties}
      />
    );
  }

  const labwareDataByHandle =
    labwareDisplayDataByTaskId[taskProperties.id] ?? {};

  const orderedHandlePairIds =
    orderedHandlePairIdsByTask[taskProperties.id] ?? [];

  const indicators = (
    <NodeIndicators
      indicators={getIndicators({
        task,
        taskDependencyErrors,
        workflowLevelErrors,
      })}
      rightAlign
    />
  );

  return (
    <TaskNode
      indicators={indicators}
      instrumentData={instrumentData}
      labwareDataByHandle={labwareDataByHandle}
      orderedHandlePairIds={orderedHandlePairIds}
      selected={selected}
      taskProperties={taskProperties}
    />
  );
};

export const ConnectedConditionNode = ({
  selected,
  properties,
  task,
}: {
  selected: boolean;
  properties: ConditionalTaskProperties;
  task: Task;
}) => {
  const workflowLevelErrorsVisible = useFlag(
    'fe.canvas-builder-workflow-errors-visible'
  );
  const workflowLevelErrors = useAppSelector((state) =>
    selectWorkflowLevelErrorsForTaskId(state, task.id)
  );

  const taskDependencyErrors = useAppSelector(selectTaskDependenciesErrors);
  const labwareDisplayDataByTaskId = useAppSelector(
    selectLabwareDisplayDataByTaskId
  );
  const orderedHandlePairIdsByTask = useAppSelector(
    selectHandlePairIdOrderByNode
  );
  const labwareDataByHandle = labwareDisplayDataByTaskId[properties.id] ?? {};

  const orderedHandlePairIds = orderedHandlePairIdsByTask[properties.id] ?? [];

  const indicators = (
    <NodeIndicators
      indicators={getIndicators({
        task,
        taskDependencyErrors,
        workflowLevelErrors: workflowLevelErrorsVisible
          ? workflowLevelErrors
          : [],
      })}
      rightAlign
    />
  );

  return (
    <ConditionNode
      properties={properties}
      selected={selected}
      labwareDataByHandle={labwareDataByHandle}
      orderedHandlePairIds={orderedHandlePairIds}
      indicators={indicators}
    />
  );
};

const ConnectedContinuousLoadingNode = ({
  properties,
  selected,
}: {
  properties: ContinuousLoadingProperties;
  selected: boolean;
}) => {
  const labwareDisplayDataByTaskId = useAppSelector(
    selectLabwareDisplayDataByTaskId
  );
  const orderedHandlePairIdsByTask = useAppSelector(
    selectHandlePairIdOrderByNode
  );
  const dataHandleIsConnectedByHandleID = useAppSelector(
    selectDataHandleIsConnectedByHandleID
  );

  const labwareDataByHandle = labwareDisplayDataByTaskId[properties.id] ?? {};
  const orderedHandlePairIds = orderedHandlePairIdsByTask[properties.id] ?? [];

  return (
    <ContinuousLoadingNode
      dataHandleIsConnectedByHandleID={dataHandleIsConnectedByHandleID}
      labwareDataByHandle={labwareDataByHandle}
      orderedHandlePairIds={orderedHandlePairIds}
      properties={properties}
      selected={selected}
    />
  );
};

export const ConnectedTaskNode = ({
  id,
  selected,
}: NodeProps): JSX.Element | null => {
  const allTasks = useAllTasks();
  const taskProperties = useAppSelector(selectTaskPropertiesStrict)[id];

  const task = allTasks[id] as Task | undefined;

  if (taskProperties === undefined || task === undefined) {
    return null;
  }

  if (
    taskProperties.propertiesType === PropertiesType.Code ||
    taskProperties.propertiesType === PropertiesType.DataExport
  ) {
    return (
      <ConnectedDataNode
        selected={selected}
        properties={taskProperties}
        task={task}
      />
    );
  }

  if (taskProperties.propertiesType === PropertiesType.Conditional) {
    return (
      <ConnectedConditionNode
        selected={selected}
        properties={taskProperties}
        task={task}
      />
    );
  }

  if (taskProperties.propertiesType === PropertiesType.ContinuousLoading) {
    return (
      <ConnectedContinuousLoadingNode
        selected={selected}
        properties={taskProperties}
      />
    );
  }

  return (
    <ConnectedInstrumentTaskNode
      id={id}
      selected={selected}
      taskProperties={taskProperties}
      task={task}
    />
  );
};
